<template>
  <div class="video-local">
    <video
      v-if="camEnabled"
      id="local-video"
      ref="video"
      class="media media--contain"
      autoplay
      muted
      playsinline
    ></video>
    <v-icon v-if="!camEnabled" icon="msf:videocam_off" :size="60" color="#949494" />
    <a-announcement v-if="camNotAvailable" icon="camera-off" :message="$t('meeting.announcement.cameraNotAvailable')" />
  </div>
</template>

<script setup>
import {computed, onBeforeUnmount, ref, watch, watchEffect} from 'vue'
import {storeToRefs} from 'pinia'
import {useTransformStore} from '@/stores'
import {useStreamStore} from '@/stores/streamStore'
import {useSnapshotOverlayStore} from '@/stores/snapshotOverlayStore'
import {useScreenShareStore} from '@/stores/screenshareStore'

const props = defineProps({mode: String})
const {camEnabled, camStream, camLost, camResolution} = storeToRefs(useStreamStore())
const screenShare = useScreenShareStore()
const {stream: screenShareStream, isScreenSharing, remoteScreenShare} = storeToRefs(screenShare)

// The camera is on but gives no picture (lost, or its capture failed; useMediaRecovery tries to get it back): say so
// instead of a plain black screen (review G6-3, on master 64af98b). After 2 s, so that the restart of a snapshot or a
// device switch does not flash it
const noPicture = computed(() => camEnabled.value && !camResolution.value && (camLost.value || !camStream.value))
const camNotAvailable = ref(false)
let noPictureTimer
watch(
  noPicture,
  missing => {
    clearTimeout(noPictureTimer)
    if (!missing) camNotAvailable.value = false
    else noPictureTimer = setTimeout(() => (camNotAvailable.value = true), 2000)
  },
  {immediate: true}
)
onBeforeUnmount(() => clearTimeout(noPictureTimer))
const transform = useTransformStore()
const videoStore = useSnapshotOverlayStore()
const {videoElement: video} = storeToRefs(videoStore)
const {zoom, x, y} = storeToRefs(transform)
watchEffect(() => {
  if (video?.value) {
    if (isScreenSharing.value) {
      video.value.srcObject = screenShareStream.value
    } else if (remoteScreenShare.value) {
      video.value.srcObject = remoteScreenShare.value
    } else if (camEnabled.value && camStream.value) {
      video.value.srcObject = camStream.value
    }
  }
})
</script>

<style lang="scss" scoped>
$video-bg: #383838;

.video-local {
  height: 100dvh;
  position: absolute;
  left: 0;
  top: 0;
  width: 100vw;
  overflow: hidden;
  background-color: $video-bg;
  z-index: $zindex-video;
  touch-action: none;
  display: flex;
  justify-content: center;
  align-items: center;
  video {
    transition-property: transform;
    transition-duration: $duration-fast;
    transform-origin: 0 0;
    transform: matrix(v-bind(zoom), 0, 0, (v-bind(zoom), v-bind(x), v-bind(y)));
  }
}
</style>
